import { useEffect, useState } from 'react';

import {
  fetchPushPublicKey,
  getPushPermission,
  isPushSupported,
  registerEmbedPushWorker,
  subscribeToPush,
  type PushSubscriptionPayload,
} from '../lib/push';

interface EmbedPushBannerProps {
  channelId: string;
  apiUrl: string;
  brandColor: string;
  /** Flag `allowPushNotifications` do canal. */
  enabled: boolean;
  /** Chat conectado — a assinatura pertence ao contato, que só existe após o registro. */
  ready: boolean;
  /** Caminho do SW hospedado pelo cliente (default `/troia-push-sw.js`). */
  swPath?: string;
  onSubscribed: (subscription: PushSubscriptionPayload) => Promise<boolean> | boolean;
}

const COOLDOWN_DAYS = 7;
const dismissKey = (channelId: string): string => `troia-push-dismissed-${channelId}`;

function isInCooldown(channelId: string): boolean {
  try {
    const dismissedAt = localStorage.getItem(dismissKey(channelId));
    if (!dismissedAt) return false;
    return Date.now() - parseInt(dismissedAt, 10) < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Banner "Ativar notificações" do EMBED (site do cliente).
 *
 * Gêmeo do `hosted/components/EnablePushBanner`, mas com estilo inline: o
 * bundle embed não tem Tailwind e roda dentro do CSS de um site de terceiro,
 * onde classe própria pode ser sobrescrita sem aviso.
 *
 * Só aparece quando o service worker de push está de fato registrado na origem
 * do cliente — sem o arquivo hospedado, nem convidamos o visitante (prometer
 * notificação que não chega é pior do que não oferecer).
 */
export function EmbedPushBanner({
  channelId,
  apiUrl,
  brandColor,
  enabled,
  ready,
  swPath,
  onSubscribed,
}: EmbedPushBannerProps) {
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled || !ready) return;
    if (!isPushSupported() || getPushPermission() !== 'default' || isInCooldown(channelId)) return;

    let cancelled = false;
    void (async () => {
      const key = await fetchPushPublicKey(apiUrl, channelId);
      if (cancelled || !key) return;

      const reg = await registerEmbedPushWorker(swPath);
      if (cancelled || !reg) return;

      setPublicKey(key);
      setRegistration(reg);
      setVisible(true);
    })();

    return () => { cancelled = true; };
  }, [enabled, ready, channelId, apiUrl, swPath]);

  if (!visible || !publicKey) return null;

  const handleEnable = async (): Promise<void> => {
    setBusy(true);
    try {
      const subscription = await subscribeToPush(publicKey, registration);
      if (subscription) await onSubscribed(subscription);
    } finally {
      setBusy(false);
      setVisible(false);
    }
  };

  const handleDismiss = (): void => {
    try {
      localStorage.setItem(dismissKey(channelId), Date.now().toString());
    } catch {
      // sem storage: some apenas nesta sessão
    }
    setVisible(false);
  };

  return (
    <div
      data-testid="widget-push-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        backgroundColor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>Ativar notificações</div>
        <div style={{ fontSize: 12, color: '#64748B' }}>
          Avisamos quando respondermos, mesmo com esta aba fechada.
        </div>
      </div>
      <button
        type="button"
        onClick={() => { void handleEnable(); }}
        disabled={busy}
        data-testid="widget-push-enable"
        style={{
          border: 'none',
          borderRadius: 6,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          color: '#FFFFFF',
          backgroundColor: brandColor,
          cursor: busy ? 'default' : 'pointer',
          opacity: busy ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {busy ? 'Ativando...' : 'Ativar'}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Agora não"
        data-testid="widget-push-dismiss"
        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4, lineHeight: 0 }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
